'use client';

import { c as _c } from "react/compiler-runtime";
import { Divider } from '@7f-ui/components/divider/divider';
import { MessageBox } from '@7f-ui/components/message-box/message-box';
import { Modal } from '@7f-ui/components/modal/modal';
import { trackCtaSelection } from '@feat/feat-tracking/cta';
import { useRouter } from 'next/navigation';
import { useState, useTransition } from 'react';
import { useExperienceOptions } from '../_hooks/use-app-experience-options';
import { useCurrentPathSearch } from '../_hooks/use-current-path-search';
import { buildTermineHref, getExperienceSearchParams, getSelectionFromFilter, initSelectedOptions, validateSelections } from '../_lib/experience-booking-utils';
import { AppExperienceContactFormLink } from './app-experience-contact-form-link';
import { useExperienceFilterContext } from './app-experience-filter-provider';
import { AppPersonSelect } from './app-person-select';
import { AppVariantSelect } from './app-variant-select';
type AppMultiVariantModalContentProps = {
  onClose: () => void;
};
function AppMultiVariantModalContent({
  onClose
}: AppMultiVariantModalContentProps) {
  const router = useRouter();
  const {
    filter,
    variants,
    experience,
    setFilter
  } = useExperienceFilterContext();
  const {
    getVariantOptions
  } = useExperienceOptions(variants);
  const {
    basePath
  } = useCurrentPathSearch();
  const [isCommittingSelection, setIsCommittingSelection] = useState(false);
  const [isNavigating, startNavigation] = useTransition();
  const [activeContent, setActiveContent] = useState<'Variant' | 'Person'>('Variant');
  const [selectedVariant, setSelectedVariant] = useState<string | undefined>(filter.selectedVariant ?? undefined);
  const [selectedOptions, setSelectedOptions] = useState(filter.selectedOptions);
  const isValidSelection = validateSelections(variants, selectedOptions, selectedVariant);
  const isSubmitting = isCommittingSelection || isNavigating;
  const handleVariantChange = (variantId: string) => {
    setSelectedVariant(variantId);
    setSelectedOptions(initSelectedOptions(getVariantOptions(variantId) ?? []));
  };
  const handleSubmit = async () => {
    const newFilter = {
      ...filter,
      selectedOptions,
      selectedVariant
    };
    setIsCommittingSelection(true);
    try {
      const params = getExperienceSearchParams(newFilter);
      const termineHref = buildTermineHref(basePath, params);
      const selection = getSelectionFromFilter(newFilter, variants);
      trackCtaSelection('take over - persons', {
        selection,
        type: 'experience'
      });
      await setFilter(newFilter);
      startNavigation(() => {
        router.push(termineHref);
      });
    } finally {
      setIsCommittingSelection(false);
    }
  };
  return <Modal.Slides activeContent={activeContent} onClose={onClose} slides={[{
    name: 'Variant',
    title: 'Option wählen',
    children: <Modal.Content renderFooter={() => <Modal.Footer>
                  <Modal.Footer.Actions>
                    <Modal.Footer.Button type="submit" variant="primary" onClick={() => {
          const selection = getSelectionFromFilter(filter, variants);
          trackCtaSelection('take over - option', {
            selection,
            type: 'experience'
          });
          setActiveContent('Person');
        }}>
                      Übernehmen
                    </Modal.Footer.Button>
                  </Modal.Footer.Actions>
                </Modal.Footer>}>
              <div className="flex h-full flex-col justify-between">
                <AppVariantSelect onChange={handleVariantChange} variants={variants} initialSelectedVariant={selectedVariant} />

                <div className="flex flex-col gap-2">
                  <Divider />
                  <AppExperienceContactFormLink experiencePublicId={experience.publicId} />
                </div>
              </div>
            </Modal.Content>
  }, {
    name: 'Person',
    title: 'Personen wählen',
    onBackClick: () => setActiveContent('Variant'),
    children: <Modal.Content renderFooter={() => <Modal.Footer>
                  <Modal.Footer.Actions>
                    <Modal.Footer.Button type="submit" variant="primary" isLoading={isSubmitting} onClick={handleSubmit} disabled={isSubmitting || !isValidSelection}>
                      Übernehmen
                    </Modal.Footer.Button>
                  </Modal.Footer.Actions>
                </Modal.Footer>}>
              <div className="flex h-full flex-col justify-between">
                <AppPersonSelect options={selectedVariant ? getVariantOptions(selectedVariant) : []} onChange={data => setSelectedOptions(data.selectedOptions)} variantId={selectedVariant ?? ''} selectedOptions={selectedOptions} priceCategory={experience.priceCategory} />

                <div className="flex flex-col gap-6">
                  <Divider />
                  <AppExperienceContactFormLink experiencePublicId={experience.publicId} />
                  <MessageBox type="info">
                    Die Auswahlmöglichkeiten und Preise hängen von der gewählten Option ab.
                  </MessageBox>
                </div>
              </div>
            </Modal.Content>
  }]} />;
}
type AppMultiVariantModalProps = {
  onClose: () => void;
  isOpen: boolean;
};
export function AppMultiVariantModal(t0) {
  const $ = _c(3);
  const {
    isOpen,
    onClose
  } = t0;
  let t1;
  if ($[0] !== isOpen || $[1] !== onClose) {
    t1 = <Modal isOpen={isOpen} initialContent="Variant" onClose={onClose}>{_temp}</Modal>;
    $[0] = isOpen;
    $[1] = onClose;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function _temp(props) {
  return <AppMultiVariantModalContent {...props} />;
}